import {
  AcademicCapIcon,
  ArrowDownTrayIcon,
  BellAlertIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  UserGroupIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolid } from '@heroicons/react/24/solid';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';
import { downloadCsv } from '../lib/csvExport';
import { getApiErrorMessage } from '../lib/apiError';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const getToken = () => localStorage.getItem('admin_token');

const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  (r) => r,
  (err) => { err.userMessage = getApiErrorMessage(err, 'Something went wrong'); return Promise.reject(err); }
);

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (d) => {
  const date = typeof d === 'string' ? new Date(d) : d;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const todayStr = fmt(new Date());

const fmtDisplay = (d) => {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmt12h = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const DAY_SHORT = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat', Sunday:'Sun' };
const DAYS_ORDER = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

// Collapses flat schedule array → "Mon – Fri (9:00 AM – 12:00 PM)"
const formatScheduleCompact = (schedule) => {
  if (!schedule?.length) return null;
  const groups = [];
  schedule.forEach((s) => {
    const last = groups[groups.length - 1];
    const sameTime    = last && last.startTime === s.startTime && last.endTime === s.endTime;
    const consecutive = last && DAYS_ORDER.indexOf(s.day) === DAYS_ORDER.indexOf(last.lastDay) + 1;
    if (sameTime && consecutive) {
      last.lastDay = s.day;
    } else {
      groups.push({ firstDay: s.day, lastDay: s.day, startTime: s.startTime, endTime: s.endTime });
    }
  });
  return groups.map(({ firstDay, lastDay, startTime, endTime }) => {
    const dayPart = firstDay === lastDay
      ? (DAY_SHORT[firstDay] || firstDay)
      : `${DAY_SHORT[firstDay] || firstDay} – ${DAY_SHORT[lastDay] || lastDay}`;
    const timePart = startTime && endTime ? ` (${fmt12h(startTime)} – ${fmt12h(endTime)})` : '';
    return `${dayPart}${timePart}`;
  }).join(', ');
};

const STATUS_CONFIG = {
  present:   { label: 'Present',  color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', bar: '#10b981' },
  absent:    { label: 'Absent',   color: 'bg-red-100 text-red-700 border-red-200',             dot: 'bg-red-500',     bar: '#ef4444' },
  late:      { label: 'Late',     color: 'bg-amber-100 text-amber-700 border-amber-200',        dot: 'bg-amber-500',   bar: '#f59e0b' },
  'half-day':{ label: 'Half Day', color: 'bg-blue-100 text-blue-700 border-blue-200',           dot: 'bg-blue-500',    bar: '#3b82f6' },
  excused:   { label: 'Excused',  color: 'bg-violet-100 text-violet-700 border-violet-200',     dot: 'bg-violet-500',  bar: '#8b5cf6' },
  'trainer-cancelled': { label: 'Trainer Cancelled Class', color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500', bar: '#f97316' },
  'network-issue': { label: 'Network Issue', color: 'bg-cyan-100 text-cyan-700 border-cyan-200', dot: 'bg-cyan-500', bar: '#06b6d4' },
  holiday:   { label: 'Holiday',  color: 'bg-gray-100 text-gray-500 border-gray-200',           dot: 'bg-gray-400',    bar: '#9ca3af' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.present;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const getWeekDates = (referenceDate) => {
  const d = new Date(referenceDate);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return date;
  });
};

// ─── Stat Card ───────────────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color, sub }) => (
  <Card className="overflow-hidden">
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
          <p className={`text-3xl font-bold mt-1 ${color}`}>{value ?? '—'}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${
          color.includes('emerald') ? 'from-emerald-100 to-emerald-200' :
          color.includes('red')     ? 'from-red-100 to-red-200' :
          color.includes('amber')   ? 'from-amber-100 to-amber-200' :
          color.includes('blue')    ? 'from-blue-100 to-blue-200' :
                                      'from-violet-100 to-violet-200'
        }`}>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AttendanceManagement() {
  usePageMeta(PAGE_META.attendance);
  // ── Selection state ──
  const [courses, setCourses]               = useState([]);
  const [batches, setBatches]               = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedBatchId, setSelectedBatchId]   = useState('');

  // ── View mode: 'week' | 'range' ──
  const [viewMode, setViewMode]             = useState('week');
  const [weekRef, setWeekRef]               = useState(new Date());
  const weekDates                           = useMemo(() => getWeekDates(weekRef), [weekRef]);

  // ── Date Range ──
  const [rangeFrom, setRangeFrom]           = useState('');
  const [rangeTo, setRangeTo]               = useState(todayStr);
  const [rangeLoaded, setRangeLoaded]       = useState(false);

  // ── Mark Attendance ──
  const [batchStudents, setBatchStudents]   = useState([]);
  const [batchLoading, setBatchLoading]     = useState(false);
  // weekAttendance[studentId][dateStr] = status
  const [weekAttendance, setWeekAttendance] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving]                 = useState(false);
  const [markSearch, setMarkSearch]         = useState('');

  // ── Summary / Today ──
  const [todaySummary, setTodaySummary]     = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // ── Trend / Analytics ──
  const [trendData, setTrendData]           = useState([]);
  const [trendLoading, setTrendLoading]     = useState(false);
  const [trendRange, setTrendRange]         = useState(30);

  // ── Student history ──
  const [studentSearch, setStudentSearch]   = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentHistory, setStudentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Export ──
  const [exportFrom, setExportFrom]         = useState('');
  const [exportTo, setExportTo]             = useState('');
  const [exporting, setExporting]           = useState(false);

  // ── Unmarked attendance alert ──
  const [unmarkedBatches, setUnmarkedBatches] = useState([]);
  const [alertDismissed, setAlertDismissed]   = useState(false);

  // ── Selected batch info (for schedule + date bounds) ──
  const selectedBatch = useMemo(
    () => batches.find(b => b._id?.toString() === selectedBatchId?.toString()) || null,
    [batches, selectedBatchId]
  );

  // ISO date strings for the batch's valid attendance range
  const batchStartStr = useMemo(
    () => selectedBatch?.startDate ? fmt(new Date(selectedBatch.startDate)) : null,
    [selectedBatch]
  );
  const batchEndStr = useMemo(
    () => selectedBatch?.endDate ? fmt(new Date(selectedBatch.endDate)) : null,
    [selectedBatch]
  );

  // Returns the schedule entry for a given date (or null if not scheduled).
  // Handles stored day names like "Monday", "monday", "Mon", "mon", "MONDAY", etc.
  const getScheduleForDate = useCallback((date) => {
    if (!selectedBatch?.schedule?.length) return null;
    const fullDay  = date.toLocaleDateString('en-US', { weekday: 'long'  }).toLowerCase(); // "monday"
    const shortDay = date.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase(); // "mon"
    return selectedBatch.schedule.find(s => {
      if (!s.day) return false;
      const stored = s.day.toLowerCase().trim();
      return stored === fullDay || stored === shortDay || fullDay.startsWith(stored.substring(0, 3));
    }) || null;
  }, [selectedBatch]);

  // ── Load courses ──
  useEffect(() => {
    api.get('/student-dashboard/courses')
      .then(r => setCourses(r.data.success ? r.data.data || [] : []))
      .catch(e => toast.error(getApiErrorMessage(e, 'Failed to load courses')));
  }, []);

  // ── Check for unmarked batches today (for alert banner) ──
  useEffect(() => {
    api.get('/attendance/alerts/unmarked-today')
      .then(r => setUnmarkedBatches(r.data?.unmarkedBatches || []))
      .catch(() => {}); // silently ignore — alert is best-effort
  }, []);

  // ── Load batches when course changes ──
  useEffect(() => {
    if (!selectedCourseId) { setBatches([]); setSelectedBatchId(''); return; }
    api.get(`/student-dashboard/courses/${selectedCourseId}/batches`)
      .then(r => setBatches(r.data.success ? r.data.data || [] : []))
      .catch(e => toast.error(getApiErrorMessage(e, 'Failed to load batches')));
  }, [selectedCourseId]);

  // ── Load students + week attendance when batch/week changes (week mode only) ──
  useEffect(() => {
    if (!selectedBatchId) { setBatchStudents([]); setWeekAttendance({}); setPendingChanges({}); return; }
    if (viewMode === 'range') return; // range mode manages its own loading
    loadBatchData();
  }, [selectedBatchId, weekRef, viewMode]); // loadBatchData intentionally excluded — it reads weekDates via closure

  // When switching back to week mode, reset range state
  useEffect(() => {
    if (viewMode === 'week') {
      setRangeLoaded(false);
      setPendingChanges({});
    }
  }, [viewMode]);

  // When batch changes, reset to week mode
  useEffect(() => {
    setViewMode('week');
    setRangeLoaded(false);
    setPendingChanges({});
    setWeekRef(new Date());
  }, [selectedBatchId]);

  const loadBatchData = async () => {
    if (!selectedBatchId) return;
    setBatchLoading(true);
    try {
      const batchResp = await api.get(`/course-dashboard/batches/${selectedBatchId}`);
      const students  = batchResp.data?.data?.students || [];
      setBatchStudents(students);

      const startDate = fmt(weekDates[0]);
      const endDate   = fmt(weekDates[6]);
      const attResp   = await api.get(`/attendance/batch/${selectedBatchId}`, {
        params: { startDate, endDate, limit: 5000 }
      });
      const records   = attResp.data?.attendances || [];

      const lookup = {};
      for (const r of records) {
        const sid     = r.studentId?._id || r.studentId;
        const dateStr = fmt(r.date);
        if (!lookup[sid]) lookup[sid] = {};
        lookup[sid][dateStr] = r.status;
      }
      setWeekAttendance(lookup);
      setPendingChanges({});
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load attendance data'));
    } finally {
      setBatchLoading(false);
    }
  };

  // ── Load attendance for the entire date range ──
  const loadRangeData = async () => {
    if (!selectedBatchId) { toast.error('Select a batch first'); return; }
    if (!rangeFrom || !rangeTo) { toast.error('Select both From and To dates'); return; }
    if (rangeFrom > rangeTo) { toast.error('From date must be before To date'); return; }

    setBatchLoading(true);
    setRangeLoaded(false);
    try {
      const batchResp = await api.get(`/course-dashboard/batches/${selectedBatchId}`);
      const students  = batchResp.data?.data?.students || [];
      setBatchStudents(students);

      const attResp = await api.get(`/attendance/batch/${selectedBatchId}`, {
        params: { startDate: rangeFrom, endDate: rangeTo, limit: 50000 }
      });
      const records = attResp.data?.attendances || [];

      const lookup = {};
      for (const r of records) {
        const sid     = r.studentId?._id || r.studentId;
        const dateStr = fmt(r.date);
        if (!lookup[sid]) lookup[sid] = {};
        lookup[sid][dateStr] = r.status;
      }
      setWeekAttendance(lookup);
      setPendingChanges({});
      setWeekRef(new Date(rangeFrom)); // Navigate to range start
      setRangeLoaded(true);
      toast.success(`Loaded attendance data from ${rangeFrom} to ${rangeTo}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load attendance data'));
    } finally {
      setBatchLoading(false);
    }
  };

  // ── Load today summary ──
  useEffect(() => {
    if (!selectedBatchId) { setTodaySummary(null); return; }
    setSummaryLoading(true);
    api.get(`/attendance/batch/${selectedBatchId}`, { params: { startDate: todayStr, endDate: todayStr, limit: 1000 } })
      .then(r => setTodaySummary(r.data?.summary || null))
      .catch(() => setTodaySummary(null))
      .finally(() => setSummaryLoading(false));
  }, [selectedBatchId]);

  // ── Load trend data ──
  useEffect(() => {
    if (!selectedBatchId) { setTrendData([]); return; }
    const endDate   = todayStr;
    const startDate = fmt(new Date(Date.now() - trendRange * 24 * 60 * 60 * 1000));
    setTrendLoading(true);
    api.get(`/attendance/batch/${selectedBatchId}`, { params: { startDate, endDate, limit: 10000 } })
      .then(r => {
        const records = r.data?.attendances || [];
        const byDate  = {};
        for (const rec of records) {
          const ds = fmt(rec.date);
          if (!byDate[ds]) byDate[ds] = {
            date: ds, present: 0, absent: 0, late: 0, excused: 0, 'half-day': 0,
            'trainer-cancelled': 0, 'network-issue': 0,
          };
          byDate[ds][rec.status] = (byDate[ds][rec.status] || 0) + 1;
        }
        setTrendData(Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)));
      })
      .catch(() => setTrendData([]))
      .finally(() => setTrendLoading(false));
  }, [selectedBatchId, trendRange]);

  // ── Week navigation ──
  const handleWeekPrev = () => {
    setWeekRef(d => {
      const n = new Date(d);
      n.setDate(n.getDate() - 7);
      if (viewMode === 'range' && rangeFrom) {
        // In range mode: don't go before rangeFrom
        if (fmt(n) < rangeFrom) return d;
      } else if (batchStartStr) {
        // In week mode: allow the week if its LAST day (Sunday) >= batchStartStr
        // This lets users navigate to weeks where the batch started mid-week
        const sunday = new Date(n);
        sunday.setDate(n.getDate() + 6);
        if (fmt(sunday) < batchStartStr) return d;
      }
      return n;
    });
  };

  const handleWeekNext = () => {
    setWeekRef(d => {
      const n = new Date(d);
      n.setDate(n.getDate() + 7);
      // Don't allow going beyond today (or rangeTo)
      const limit = viewMode === 'range' && rangeTo ? rangeTo : todayStr;
      if (fmt(n) > limit) return d;
      return n;
    });
  };

  // Jump to the week containing the given date string
  const handleJumpToDate = (dateStr) => {
    if (!dateStr) return;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return;
    // Clamp to valid range
    const target = fmt(d);
    if (target > todayStr) return;
    if (batchStartStr && target < batchStartStr) return;
    setWeekRef(d);
  };

  // ── Cell click — cycle status ──
  const cycleStatus = (studentId, dateStr) => {
    if (dateStr > todayStr) { toast.info('Cannot mark future dates'); return; }
    if (batchStartStr && dateStr < batchStartStr) { toast.info('Cannot mark attendance before the batch start date'); return; }
    const order = ['present', 'absent', 'late', 'half-day', 'excused', 'trainer-cancelled', 'network-issue'];
    const key   = `${studentId}_${dateStr}`;
    const current = pendingChanges[key] ?? weekAttendance[studentId]?.[dateStr] ?? null;
    const next = current
      ? order[(order.indexOf(current) + 1) % order.length]
      : 'present';
    setPendingChanges(p => ({ ...p, [key]: next }));
  };

  const getCellStatus = (studentId, dateStr) =>
    pendingChanges[`${studentId}_${dateStr}`] ?? weekAttendance[studentId]?.[dateStr] ?? null;

  // ── Bulk set all students on a date ──
  const markAllOnDate = (dateStr, status) => {
    if (dateStr > todayStr) { toast.info('Cannot mark future dates'); return; }
    if (batchStartStr && dateStr < batchStartStr) { toast.info('Cannot mark attendance before the batch start date'); return; }
    const changes = {};
    for (const s of batchStudents) {
      changes[`${s._id}_${dateStr}`] = status;
    }
    setPendingChanges(p => ({ ...p, ...changes }));
  };

  // ── Save pending changes (uses bulk-upsert to handle past records) ──
  const saveChanges = async () => {
    if (!Object.keys(pendingChanges).length) { toast.info('No changes to save'); return; }
    setSaving(true);

    const grouped = {};
    for (const [key, status] of Object.entries(pendingChanges)) {
      const [studentId, dateStr] = key.split(/_(?=[^_]+$)/);
      if (!grouped[dateStr]) grouped[dateStr] = [];
      grouped[dateStr].push({ studentId, status });
    }

    let savedCount = 0, errorCount = 0;
    for (const [dateStr, records] of Object.entries(grouped)) {
      try {
        let sessionId;
        try {
          const sessResp = await api.post('/attendance/sessions', {
            batchId: selectedBatchId,
            date: dateStr,
          });
          sessionId = sessResp.data?.session?._id;
        } catch (e) {
          if (e.response?.status === 409) {
            const s2 = await api.get(`/attendance/sessions/batch/${selectedBatchId}`, {
              params: { startDate: dateStr, endDate: dateStr }
            });
            sessionId = s2.data?.sessions?.[0]?._id;
          }
        }
        // Use bulk-upsert so past/already-marked records are updated, not rejected
        await api.post('/attendance/bulk-upsert', {
          batchId: selectedBatchId,
          date: dateStr,
          attendanceRecords: records.map(r => ({ studentId: r.studentId, status: r.status })),
          ...(sessionId ? { sessionId } : {}),
        });
        savedCount += records.length;
      } catch (e) {
        console.error('Save error for date', dateStr, e);
        errorCount++;
      }
    }

    if (errorCount === 0) {
      toast.success(`Saved ${savedCount} attendance record${savedCount > 1 ? 's' : ''}`);
    } else {
      toast.warning(`Saved ${savedCount} records; ${errorCount} date(s) failed`);
    }

    if (viewMode === 'range' && rangeLoaded) {
      await loadRangeData();
    } else {
      await loadBatchData();
    }
    setSaving(false);
  };

  // ── Student history ──
  const loadStudentHistory = async (student) => {
    setSelectedStudent(student);
    setHistoryLoading(true);
    try {
      const params = { limit: 90 };
      if (selectedBatchId) params.batchId = selectedBatchId;
      const r = await api.get(`/attendance/student/${student._id}`, { params });
      setStudentHistory(r.data?.attendances || []);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load student history'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const studentStats = useMemo(() => {
    if (!studentHistory.length) return null;
    const counts = {
      present: 0, absent: 0, late: 0, 'half-day': 0, excused: 0,
      'trainer-cancelled': 0, 'network-issue': 0,
    };
    for (const r of studentHistory) counts[r.status] = (counts[r.status] || 0) + 1;
    const total = studentHistory.length - counts['trainer-cancelled'] - counts['network-issue'];
    const pct   = total > 0 ? Math.round(((counts.present + counts.late + counts['half-day'] * 0.5) / total) * 100) : 0;
    return { counts, total, pct };
  }, [studentHistory]);

  const pieData = studentStats
    ? Object.entries(studentStats.counts)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({ name: STATUS_CONFIG[k]?.label || k, value: v, fill: STATUS_CONFIG[k]?.bar }))
    : [];

  // ── Export ──
  const handleExport = async () => {
    if (!selectedBatchId) { toast.error('Select a batch first'); return; }
    setExporting(true);
    try {
      const params = { limit: 100000 };
      if (exportFrom) params.startDate = exportFrom;
      if (exportTo)   params.endDate   = exportTo;
      const r = await api.get(`/attendance/batch/${selectedBatchId}`, { params });
      const rows = (r.data?.attendances || []).map(a => [
        a.date ? new Date(a.date).toLocaleDateString('en-IN') : '',
        a.studentId?.name || '',
        a.status,
        a.reason || a.notes || '',
        a.markedBy?.name || '',
      ]);
      downloadCsv(['Date', 'Student', 'Status', 'Reason/Notes', 'Marked By'], rows,
        `attendance-${selectedBatchId}-${exportFrom || 'all'}-${exportTo || 'all'}.csv`);
      toast.success(`Exported ${rows.length} records`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  // ── Filtered students ──
  const filteredStudents = batchStudents.filter(s =>
    !markSearch || s.name?.toLowerCase().includes(markSearch.toLowerCase())
  );

  const filteredHistoryStudents = batchStudents.filter(s =>
    !studentSearch || s.name?.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const pendingCount = Object.keys(pendingChanges).length;

  // ── Week range label ──
  const weekRangeLabel = useMemo(() => {
    const s = weekDates[0].toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    const e = weekDates[6].toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${s} — ${e}`;
  }, [weekDates]);

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50/30 p-4 sm:p-6 space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Management</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track, mark, and analyse student attendance</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CalendarDaysIcon className="h-4 w-4" />
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      {/* ── Unmarked Attendance Alert Banner ── */}
      {!alertDismissed && unmarkedBatches.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <BellAlertIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5 animate-pulse" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              Attendance not marked for {unmarkedBatches.length} batch{unmarkedBatches.length > 1 ? 'es' : ''} today
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {unmarkedBatches.map(b => (
                <span
                  key={b.batchId}
                  className="inline-flex items-center gap-1 text-xs bg-amber-100 border border-amber-300 text-amber-800 rounded-full px-2.5 py-1 font-medium"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  {b.batchName}
                  <span className="text-amber-500">· {b.courseName}</span>
                  <span className="text-amber-400">({b.studentCount} students)</span>
                </span>
              ))}
            </div>
            <p className="text-xs text-amber-600 mt-1.5">
              Select the batch above and mark attendance. Staff will receive an automated email alert at 6 PM if still unmarked.
            </p>
          </div>
          <button
            onClick={() => setAlertDismissed(true)}
            className="text-amber-500 hover:text-amber-700 transition-colors flex-shrink-0"
            title="Dismiss alert"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Course / Batch Selector ── */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Course</Label>
              <Select value={selectedCourseId} onValueChange={v => { setSelectedCourseId(v); setSelectedBatchId(''); }}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Select course…" /></SelectTrigger>
                <SelectContent>
                  {courses.map(c => <SelectItem key={c._id} value={c._id}>{c.courseName || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Batch</Label>
              <Select value={selectedBatchId} onValueChange={setSelectedBatchId} disabled={!selectedCourseId}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Select batch…" /></SelectTrigger>
                <SelectContent>
                  {batches.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Batch info strip */}
          {selectedBatch && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              {/* Dates row */}
              <div className="flex flex-wrap gap-4 mb-3">
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                  <span className="text-gray-400">Start date:</span>
                  <span className="font-semibold text-gray-700">{fmtDisplay(selectedBatch.startDate)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                  <span className="text-gray-400">End date:</span>
                  <span className="font-semibold text-gray-700">{fmtDisplay(selectedBatch.endDate)}</span>
                </div>
                {selectedBatch.schedule?.length > 0 && (() => {
                  const compact = formatScheduleCompact(selectedBatch.schedule);
                  return compact ? (
                    <div className="flex items-center gap-1.5 text-xs text-gray-600">
                      <ClockIcon className="h-3 w-3 text-violet-500 flex-shrink-0" />
                      <span className="font-medium text-violet-700">{compact}</span>
                    </div>
                  ) : null;
                })()}
                <div className="flex items-center gap-1.5 text-xs">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    selectedBatch.status === 'active'    ? 'bg-emerald-100 text-emerald-700' :
                    selectedBatch.status === 'upcoming'  ? 'bg-blue-100 text-blue-700' :
                                                           'bg-gray-100 text-gray-500'
                  }`}>
                    {selectedBatch.status || 'unknown'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <span className="font-medium text-gray-600">{selectedBatch.mode}</span> · {selectedBatch.assignedStudents ?? batchStudents.length} students
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Today's Stats ── */}
      {selectedBatchId && (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          <StatCard title="Present Today"  value={summaryLoading ? '…' : todaySummary?.present  ?? 0} icon={CheckCircleIcon}       color="text-emerald-600" sub={`of ${todaySummary ? (todaySummary.eligibleTotal ?? todaySummary.total ?? 0) : batchStudents.length} students`} />
          <StatCard title="Absent"         value={summaryLoading ? '…' : todaySummary?.absent   ?? 0} icon={XCircleIcon}           color="text-red-600"     sub="today" />
          <StatCard title="Late"           value={summaryLoading ? '…' : todaySummary?.late     ?? 0} icon={ClockIcon}             color="text-amber-600"   sub="today" />
          <StatCard title="Excused / Half" value={summaryLoading ? '…' : (todaySummary?.excused ?? 0) + (todaySummary?.['half-day'] ?? 0)} icon={ExclamationCircleIcon} color="text-violet-600" sub="today" />
          <StatCard title="Trainer Cancelled" value={summaryLoading ? '…' : todaySummary?.trainerCancelled ?? 0} icon={XCircleIcon} color="text-orange-600" sub="today" />
          <StatCard title="Network Issue" value={summaryLoading ? '…' : todaySummary?.networkIssue ?? 0} icon={ExclamationCircleIcon} color="text-cyan-600" sub="today" />
        </div>
      )}

      {/* ── Tabs ── */}
      <Tabs defaultValue="mark" className="space-y-4">
        <TabsList className="bg-white border shadow-sm">
          <TabsTrigger value="mark"    className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">
            <CheckSolid className="h-4 w-4 mr-1.5" /> Mark Attendance
          </TabsTrigger>
          <TabsTrigger value="trends"  className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">
            <CalendarDaysIcon className="h-4 w-4 mr-1.5" /> Trends & Reports
          </TabsTrigger>
          <TabsTrigger value="student" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">
            <UserGroupIcon className="h-4 w-4 mr-1.5" /> Student View
          </TabsTrigger>
        </TabsList>

        {/* ════════════════════════════════════════════════════════
            TAB 1 — MARK ATTENDANCE
        ════════════════════════════════════════════════════════ */}
        <TabsContent value="mark">
          {!selectedBatchId ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-16 text-center text-gray-400">
                <AcademicCapIcon className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                <p className="font-medium">Select a course and batch to mark attendance</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">

              {/* ── Mode toggle ── */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setViewMode('week')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                        viewMode === 'week'
                          ? 'bg-violet-600 text-white border-violet-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <CalendarDaysIcon className="h-4 w-4" /> Week View
                    </button>
                    <button
                      onClick={() => setViewMode('range')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                        viewMode === 'range'
                          ? 'bg-violet-600 text-white border-violet-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <SparklesIcon className="h-4 w-4" /> Past / Date Range
                    </button>
                  </div>

                  {/* Date range picker (only in range mode) */}
                  {viewMode === 'range' && (
                    <div className="flex flex-wrap items-end gap-3 pt-1 border-t border-gray-100">
                      <div>
                        <Label className="text-xs text-gray-500 mb-1 block">
                          From Date {batchStartStr && <span className="text-gray-400">(min: {batchStartStr})</span>}
                        </Label>
                        <Input
                          type="date"
                          value={rangeFrom}
                          min={batchStartStr || undefined}
                          max={rangeTo || todayStr}
                          onChange={e => setRangeFrom(e.target.value)}
                          className="bg-white w-40"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 mb-1 block">To Date</Label>
                        <Input
                          type="date"
                          value={rangeTo}
                          min={rangeFrom || batchStartStr || undefined}
                          max={todayStr}
                          onChange={e => setRangeTo(e.target.value)}
                          className="bg-white w-40"
                        />
                      </div>
                      <Button
                        onClick={loadRangeData}
                        disabled={batchLoading || !rangeFrom || !rangeTo}
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                      >
                        {batchLoading ? (
                          <span className="flex items-center gap-1.5">
                            <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            Loading…
                          </span>
                        ) : (
                          <><CalendarDaysIcon className="h-4 w-4 mr-1.5" />Load Attendance</>
                        )}
                      </Button>
                      {rangeLoaded && (
                        <span className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-1.5 rounded-lg">
                          Showing {rangeFrom} → {rangeTo} · navigate week by week below
                        </span>
                      )}
                      <p className="w-full text-xs text-gray-400 -mt-1">
                        Use this mode to mark or correct attendance for past dates.
                        Existing records will be updated, new ones will be created.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ── Week nav + save ── */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleWeekPrev}>
                    <ChevronLeftIcon className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-semibold text-gray-700 min-w-[200px] text-center">
                    {weekRangeLabel}
                  </span>
                  <Button variant="outline" size="sm" onClick={handleWeekNext}>
                    <ChevronRightIcon className="h-4 w-4" />
                  </Button>
                  {viewMode === 'week' && (
                    <Button variant="ghost" size="sm" className="text-violet-600 text-xs" onClick={() => setWeekRef(new Date())}>
                      Today
                    </Button>
                  )}
                  {viewMode === 'range' && rangeLoaded && (
                    <Button variant="ghost" size="sm" className="text-violet-600 text-xs" onClick={() => setWeekRef(new Date(rangeFrom))}>
                      Range Start
                    </Button>
                  )}
                  {/* Jump to any past date */}
                  <div className="flex items-center gap-1.5 ml-1 border-l border-gray-200 pl-2">
                    <span className="text-xs text-gray-400 hidden sm:inline">Go to:</span>
                    <input
                      type="date"
                      title="Jump to date"
                      min={batchStartStr || undefined}
                      max={todayStr}
                      onChange={e => handleJumpToDate(e.target.value)}
                      className="h-8 w-36 rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {pendingCount > 0 && (
                    <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                      {pendingCount} unsaved change{pendingCount > 1 ? 's' : ''}
                    </span>
                  )}
                  <Button
                    onClick={saveChanges}
                    disabled={saving || pendingCount === 0}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    {saving ? (
                      <span className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        Saving…
                      </span>
                    ) : (
                      <><CheckSolid className="h-4 w-4 mr-1.5" />Save Attendance</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-3 text-xs">
                {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'holiday').map(([k, v]) => (
                  <span key={k} className={`flex items-center gap-1 px-2 py-1 rounded-full border ${v.color}`}>
                    <span className={`w-2 h-2 rounded-full ${v.dot}`} />{v.label}
                  </span>
                ))}
                <span className="flex items-center gap-1 text-gray-400 italic">· Click a cell to cycle · Future dates locked</span>
              </div>

              {/* Search */}
              <div className="relative w-full sm:w-64">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search students…"
                  value={markSearch}
                  onChange={e => setMarkSearch(e.target.value)}
                  className="pl-9 bg-white"
                />
              </div>

              {/* Show prompt when in range mode but data not loaded yet */}
              {viewMode === 'range' && !rangeLoaded ? (
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-16 text-center text-gray-400">
                    <CalendarDaysIcon className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                    <p className="font-medium">Select a date range and click "Load Attendance"</p>
                    <p className="text-sm mt-1">You can mark or correct attendance for any past date</p>
                  </CardContent>
                </Card>
              ) : batchLoading ? (
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-16 text-center text-gray-400">
                    <span className="h-8 w-8 rounded-full border-4 border-violet-200 border-t-violet-600 animate-spin inline-block mb-3" />
                    <p>Loading attendance data…</p>
                  </CardContent>
                </Card>
              ) : (
                /* ── Weekly Grid ── */
                <Card className="border-0 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gradient-to-r from-violet-600 to-violet-700 text-white">
                          <th className="text-left px-4 py-3 font-medium min-w-[180px] sticky left-0 bg-violet-600 z-10">Student</th>
                          {weekDates.map(d => {
                            const ds          = fmt(d);
                            const isToday     = ds === todayStr;
                            const isFuture    = ds > todayStr;
                            const isPreBatch  = batchStartStr && ds < batchStartStr;
                            const isLocked    = isFuture || isPreBatch;
                            const schedule    = getScheduleForDate(d);
                            const isScheduled = !!schedule;
                            const hasScheduleDef = selectedBatch?.schedule?.length > 0;

                            return (
                              <th key={ds} className={`px-2 py-2 text-center min-w-[110px] ${isLocked ? 'opacity-40' : ''}`}>
                                <div className={`flex flex-col items-center gap-0.5 ${isToday ? 'bg-white/20 rounded-lg p-1' : ''}`}>
                                  <span className="text-xs opacity-75">{d.toLocaleDateString('en-IN', { weekday: 'short' })}</span>
                                  <span className="font-bold">{d.getDate()}</span>
                                  {isToday    && <span className="text-[9px] bg-white text-violet-700 rounded px-1 font-semibold">TODAY</span>}
                                  {isFuture   && <span className="text-[9px] bg-white/20 rounded px-1">FUTURE</span>}
                                  {isPreBatch && <span className="text-[9px] bg-white/20 rounded px-1">PRE-BATCH</span>}
                                  {!isLocked && hasScheduleDef && (
                                    isScheduled ? (
                                      <span className="text-[9px] bg-white/90 text-emerald-700 rounded px-1.5 py-0.5 font-semibold leading-tight">
                                        {schedule.startTime && schedule.endTime
                                          ? `${schedule.startTime}–${schedule.endTime}`
                                          : '✓ Scheduled'}
                                      </span>
                                    ) : (
                                      <span className="text-[9px] bg-white/90 text-orange-600 rounded px-1.5 py-0.5 font-semibold leading-tight flex items-center gap-0.5">
                                        <SparklesIcon className="h-2 w-2" /> Extra
                                      </span>
                                    )
                                  )}
                                </div>
                              </th>
                            );
                          })}
                          <th className="px-3 py-3 text-center min-w-[80px]">Week %</th>
                        </tr>

                        {/* Quick mark row */}
                        <tr className="bg-violet-50 border-b">
                          <td className="px-4 py-2 text-xs text-gray-500 font-medium sticky left-0 bg-violet-50 z-10">Mark all →</td>
                          {weekDates.map(d => {
                            const ds         = fmt(d);
                            const isLocked   = ds > todayStr || (batchStartStr && ds < batchStartStr);
                            return (
                              <td key={ds} className="px-2 py-2 text-center">
                                {!isLocked && (
                                  <div className="flex justify-center gap-1">
                                    <button
                                      onClick={() => markAllOnDate(ds, 'present')}
                                      className="w-6 h-6 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-bold transition-colors"
                                      title="Mark all Present"
                                    >P</button>
                                    <button
                                      onClick={() => markAllOnDate(ds, 'absent')}
                                      className="w-6 h-6 rounded bg-red-100 hover:bg-red-200 text-red-700 text-xs font-bold transition-colors"
                                      title="Mark all Absent"
                                    >A</button>
                                    <button
                                      onClick={() => markAllOnDate(ds, 'trainer-cancelled')}
                                      className="w-5 h-5 rounded bg-orange-100 hover:bg-orange-200 text-orange-700 text-[9px] font-bold transition-colors"
                                      title="Mark class cancelled by trainer"
                                    >TC</button>
                                    <button
                                      onClick={() => markAllOnDate(ds, 'network-issue')}
                                      className="w-5 h-5 rounded bg-cyan-100 hover:bg-cyan-200 text-cyan-700 text-[9px] font-bold transition-colors"
                                      title="Mark network issue"
                                    >N</button>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          <td />
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-50">
                        {filteredStudents.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-center py-10 text-gray-400">No students found</td>
                          </tr>
                        ) : filteredStudents.map((student, idx) => {
                          const sid = student._id;
                          let weekPresent = 0, weekTotal = 0;
                          for (const d of weekDates) {
                            const ds = fmt(d);
                            if (ds > todayStr) continue;
                            const st = getCellStatus(sid, ds);
                            if (st && !['trainer-cancelled', 'network-issue'].includes(st)) {
                              weekTotal++;
                              if (st === 'present' || st === 'late') weekPresent++;
                              if (st === 'half-day') weekPresent += 0.5;
                            }
                          }
                          const weekPct = weekTotal > 0 ? Math.round((weekPresent / weekTotal) * 100) : null;

                          return (
                            <tr key={sid} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-violet-50/40 transition-colors`}>
                              <td className={`px-4 py-3 sticky left-0 z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                    {student.name?.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-800 text-xs leading-tight">{student.name}</p>
                                    <p className="text-[10px] text-gray-400 leading-tight">{student.email}</p>
                                  </div>
                                </div>
                              </td>

                              {weekDates.map(d => {
                                const ds         = fmt(d);
                                const isFuture   = ds > todayStr;
                                const isPreBatch = batchStartStr && ds < batchStartStr;
                                const isLocked   = isFuture || isPreBatch;
                                const status     = getCellStatus(sid, ds);
                                const isPending  = pendingChanges[`${sid}_${ds}`] !== undefined;
                                const isToday    = ds === todayStr;
                                const cfg        = status ? STATUS_CONFIG[status] : null;
                                const lockTitle  = isFuture ? 'Cannot mark future dates'
                                                 : isPreBatch ? 'Cannot mark before batch start date'
                                                 : `Click to mark/cycle status for ${student.name}`;

                                return (
                                  <td key={ds} className="px-2 py-2 text-center">
                                    <button
                                      onClick={() => !isLocked && cycleStatus(sid, ds)}
                                      disabled={isLocked}
                                      className={`
                                        w-full min-w-[90px] py-1.5 px-2 rounded-lg border text-xs font-medium transition-all
                                        ${isLocked
                                          ? 'border-dashed border-gray-100 text-gray-200 cursor-not-allowed bg-gray-50/50'
                                          : cfg
                                            ? `${cfg.color} ${isPending ? 'ring-2 ring-offset-1 ring-violet-400 shadow-sm' : ''} cursor-pointer`
                                            : 'border-dashed border-gray-200 text-gray-300 hover:border-violet-300 hover:text-violet-400 cursor-pointer'
                                        }
                                        ${isToday && !isLocked ? 'ring-1 ring-violet-300' : ''}
                                      `}
                                      title={lockTitle}
                                    >
                                      {isLocked ? '—' : cfg ? (
                                        <span className="flex items-center justify-center gap-1">
                                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                                          {cfg.label}
                                          {isPending && <span className="w-1 h-1 rounded-full bg-violet-500" />}
                                        </span>
                                      ) : '—'}
                                    </button>
                                  </td>
                                );
                              })}

                              <td className="px-3 py-2 text-center">
                                {weekPct !== null ? (
                                  <span className={`text-xs font-bold ${weekPct >= 75 ? 'text-emerald-600' : weekPct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                                    {weekPct}%
                                  </span>
                                ) : <span className="text-gray-300 text-xs">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* ════════════════════════════════════════════════════════
            TAB 2 — TRENDS & REPORTS
        ════════════════════════════════════════════════════════ */}
        <TabsContent value="trends">
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 flex flex-col sm:flex-row items-end gap-4">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Show last</Label>
                  <Select value={String(trendRange)} onValueChange={v => setTrendRange(Number(v))}>
                    <SelectTrigger className="w-36 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1" />
                <div className="flex items-end gap-2 flex-wrap">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">From</Label>
                    <Input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} className="bg-white w-36" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">To</Label>
                    <Input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)} className="bg-white w-36" />
                  </div>
                  <Button onClick={handleExport} disabled={exporting || !selectedBatchId} variant="outline" className="border-violet-300 text-violet-700">
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    {exporting ? 'Exporting…' : 'Export CSV'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {!selectedBatchId ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-16 text-center text-gray-400">
                  <CalendarDaysIcon className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                  <p>Select a batch to view attendance trends</p>
                </CardContent>
              </Card>
            ) : trendLoading ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-16 text-center text-gray-400">
                  <span className="h-8 w-8 rounded-full border-4 border-violet-200 border-t-violet-600 animate-spin inline-block mb-3" />
                  <p>Loading trend data…</p>
                </CardContent>
              </Card>
            ) : trendData.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-16 text-center text-gray-400">
                  <p>No attendance data found for this period</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="border-0 shadow-sm lg:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Daily Attendance Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={trendData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 10 }}
                          tickFormatter={d => {
                            const date = new Date(d);
                            return `${date.getDate()}/${date.getMonth() + 1}`;
                          }}
                        />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(v, name) => [v, name]}
                          labelFormatter={d => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="present"  name="Present"  fill="#10b981" radius={[2,2,0,0]} stackId="a" />
                        <Bar dataKey="late"     name="Late"     fill="#f59e0b" radius={[0,0,0,0]} stackId="a" />
                        <Bar dataKey="half-day" name="Half Day" fill="#3b82f6" radius={[0,0,0,0]} stackId="a" />
                        <Bar dataKey="excused"  name="Excused"  fill="#8b5cf6" radius={[0,0,0,0]} stackId="a" />
                        <Bar dataKey="absent"   name="Absent"   fill="#ef4444" radius={[2,2,0,0]} stackId="a" />
                        <Bar dataKey="trainer-cancelled" name="Trainer Cancelled Class" fill="#f97316" stackId="a" />
                        <Bar dataKey="network-issue" name="Network Issue" fill="#06b6d4" stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Overall Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const totals = trendData.reduce((acc, d) => {
                        for (const k of ['present','absent','late','half-day','excused','trainer-cancelled','network-issue']) acc[k] = (acc[k] || 0) + (d[k] || 0);
                        return acc;
                      }, {});
                      const grand = Object.values(totals).reduce((a, b) => a + b, 0);
                      const pd = Object.entries(totals).filter(([, v]) => v > 0).map(([k, v]) => ({
                        name: STATUS_CONFIG[k]?.label || k, value: v, fill: STATUS_CONFIG[k]?.bar, pct: Math.round(v / grand * 100)
                      }));
                      return (
                        <>
                          <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                              <Pie data={pd} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={2}>
                                {pd.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                              </Pie>
                              <Tooltip formatter={(v, n) => [`${v} (${pd.find(d => d.name === n)?.pct}%)`, n]} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="space-y-1.5 mt-2">
                            {pd.map(d => (
                              <div key={d.name} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-1.5">
                                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: d.fill }} />
                                  <span className="text-gray-600">{d.name}</span>
                                </div>
                                <span className="font-semibold text-gray-800">{d.value} <span className="text-gray-400 font-normal">({d.pct}%)</span></span>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ════════════════════════════════════════════════════════
            TAB 3 — STUDENT VIEW
        ════════════════════════════════════════════════════════ */}
        <TabsContent value="student">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Students</CardTitle>
                <div className="relative mt-2">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search student…"
                    value={studentSearch}
                    onChange={e => setStudentSearch(e.target.value)}
                    className="pl-9 bg-white"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {!selectedBatchId ? (
                  <p className="text-sm text-gray-400 p-4 text-center">Select a batch first</p>
                ) : (
                  <div className="divide-y max-h-[500px] overflow-y-auto">
                    {filteredHistoryStudents.map(s => (
                      <button
                        key={s._id}
                        onClick={() => loadStudentHistory(s)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-violet-50 transition-colors ${selectedStudent?._id === s._id ? 'bg-violet-50 border-r-2 border-violet-600' : ''}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {s.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{s.name}</p>
                          <p className="text-xs text-gray-400 truncate">{s.email}</p>
                        </div>
                      </button>
                    ))}
                    {filteredHistoryStudents.length === 0 && (
                      <p className="text-sm text-gray-400 p-4 text-center">No students found</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="lg:col-span-2 space-y-4">
              {!selectedStudent ? (
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-20 text-center text-gray-400">
                    <UserGroupIcon className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                    <p>Select a student to view their attendance history</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card className="border-0 shadow-sm bg-gradient-to-r from-violet-600 to-violet-700 text-white">
                    <CardContent className="p-5 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold">
                          {selectedStudent.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{selectedStudent.name}</h3>
                          <p className="text-violet-200 text-sm">{selectedStudent.email}</p>
                        </div>
                      </div>
                      {studentStats && (
                        <div className="text-right">
                          <p className="text-3xl font-bold">{studentStats.pct}%</p>
                          <p className="text-violet-200 text-xs">Attendance rate</p>
                          <p className="text-violet-200 text-xs">{studentStats.total} sessions recorded</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {studentStats && (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {Object.entries(studentStats.counts).filter(([,v]) => v > 0).map(([k, v]) => {
                        const cfg = STATUS_CONFIG[k];
                        return (
                          <div key={k} className={`rounded-xl border p-3 text-center ${cfg.color}`}>
                            <p className="text-2xl font-bold">{v}</p>
                            <p className="text-xs mt-0.5">{cfg.label}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {pieData.length > 0 && (
                      <Card className="border-0 shadow-sm">
                        <CardHeader className="pb-2"><CardTitle className="text-sm">Status Breakdown</CardTitle></CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={160}>
                            <PieChart>
                              <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} dataKey="value" paddingAngle={3}>
                                {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="space-y-1 mt-1">
                            {pieData.map(d => (
                              <div key={d.name} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-1.5">
                                  <span className="w-2 h-2 rounded-full" style={{ background: d.fill }} />
                                  <span className="text-gray-600">{d.name}</span>
                                </div>
                                <span className="font-semibold">{d.value}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <Card className="border-0 shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Recent Records</CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        {historyLoading ? (
                          <p className="text-center py-8 text-gray-400 text-sm">Loading…</p>
                        ) : studentHistory.length === 0 ? (
                          <p className="text-center py-8 text-gray-400 text-sm">No records found</p>
                        ) : (
                          <div className="divide-y max-h-[280px] overflow-y-auto">
                            {studentHistory.slice(0, 30).map(r => (
                              <div key={r._id} className="flex items-center justify-between px-4 py-2.5">
                                <div>
                                  <p className="text-xs font-medium text-gray-700">
                                    {new Date(r.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </p>
                                  {r.batchId?.name && (
                                    <p className="text-[10px] text-gray-400">{r.batchId.name}</p>
                                  )}
                                  {(r.reason || r.notes) && (
                                    <p className="text-[10px] text-gray-400 max-w-[160px] truncate">{r.reason || r.notes}</p>
                                  )}
                                </div>
                                <StatusBadge status={r.status} />
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
